import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import api, { extractError } from '@/shared/api/client'

// Shared contract value (both repos) — Support Codio scope.md L28.3+.
const TICKET_KEY_REGEX = /^[A-Z]{2,5}-[1-9][0-9]{0,9}$/
// Same shape the backend's `masterUuid` (zod `z.string().guid()`) enforces: 8-4-4-4-12 hex, NO version/variant
// check. Client ids are UUIDv7 (hotfix 2026-10-07) AND the Master client is the sentinel
// 00000000-0000-0000-0000-000000000001 (version 0) — a version-checking pattern refused every Master-client
// user's deep link with "Support isn't available" (hotfix 2026-10-09). The id is still verified server-side.
const UUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

type Phase = 'pending' | 'redirecting' | 'error' | 'rate_limited'

/**
 * `/support/open?ticket=<KEY>&client=<uuid>` — a customer deep link into a
 * specific Support Codio ticket (Support Codio scope.md L28.5/L28.6).
 *
 * Sits inside the authenticated AppShell branch, so a logged-out visitor is
 * bounced to `/auth/login?next=/support/open?...` first and lands back here
 * once signed in (AppShell preserves pathname + search + hash as `next`).
 *
 * On mount: validate both params locally (same rules the backend enforces) —
 * an invalid param shows the neutral error page and makes NO request. Valid
 * params POST `/support/handoff` with `{ clientId, ticketKey }`; the returned
 * `url` is checked to be http(s) (never `javascript:` or similar) before a
 * same-tab `window.location.replace` — no popup, no new tab.
 *
 * A `useRef` guard makes this exactly-once even under StrictMode's dev
 * double-mount, so one page load never mints two single-use tokens.
 */
export function SupportOpenPage() {
  const [searchParams] = useSearchParams()
  const ticketKey = searchParams.get('ticket')
  const clientId = searchParams.get('client')
  // Pure/derived from the URL — computed during render, not as an effect's
  // first act, so an invalid link never needs a setState-in-effect round trip
  // (and never fires a request in the first place).
  const paramsValid = !!ticketKey && TICKET_KEY_REGEX.test(ticketKey) && !!clientId && UUID_REGEX.test(clientId)

  const [phase, setPhase] = useState<Phase>(paramsValid ? 'pending' : 'error')
  const requested = useRef(false)

  useEffect(() => {
    if (!paramsValid) return
    if (requested.current) return
    requested.current = true

    api
      .post<{ data: { url: string } }>('/support/handoff', { clientId, ticketKey })
      .then((res) => {
        const url = res.data.data.url
        if (!/^https?:$/.test(new URL(url).protocol)) {
          throw new Error('Support returned a non-http(s) URL')
        }
        setPhase('redirecting')
        window.location.replace(url)
      })
      .catch((err) => {
        setPhase(extractError(err).status === 429 ? 'rate_limited' : 'error')
      })
  }, [paramsValid, clientId, ticketKey])

  if (phase === 'rate_limited') {
    return <Message text="Too many attempts, try again in a minute." />
  }

  if (phase === 'error') {
    return (
      <Message text="Support isn't available for this link. Open Support from MediCodio, or ask your administrator.">
        <Link to="/" className="btn btn-primary btn-md" style={{ marginTop: 'var(--sp-4)' }}>
          Back to Home
        </Link>
      </Message>
    )
  }

  // 'pending' / 'redirecting' — same spinner; the tab is about to navigate away.
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--bg)',
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          border: '3px solid var(--primary-200)',
          borderTopColor: 'var(--primary)',
          animation: 'spin 0.8s linear infinite',
        }}
      />
    </div>
  )
}

function Message({ text, children }: { text: string; children?: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 'var(--sp-4)',
        background: 'var(--bg)',
        textAlign: 'center',
        padding: 'var(--sp-6)',
      }}
    >
      <p className="text-h3">{text}</p>
      {children}
    </div>
  )
}
